import React, { Suspense, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "context";
import { useUserActivityTracker } from "hooks";
import { Loading } from "core";
import ProductPriceListPage from "modules/products/productpricelist/ProductPriceListPage";
import ProductToBuyListPage from "modules/products/producttobuylist/ProductToBuyListPage";
import {
  AuthRoutes,
  AuthRouting,
  RouteArrayType,
  RoutesArray,
  isAuthRoute,
} from "./routes";
import { AuthLayout } from "./layout/AuthLayout";
import { Layout } from "./layout/Layout";
import { NoRouteAccess } from "./layout/NoRouteAccess";
import { NoRouteMatch } from "./layout/NoRouteMatch";

const App: React.FC = () => {
  const authContext = useAuthContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useUserActivityTracker();

  const isPriceListRoute = pathname.includes("preisliste");

  const isBuyingRoute = pathname.includes("einkauf");

  const getRoutes = (subNavigations: RouteArrayType[]): JSX.Element[] => {
    return subNavigations.flatMap((route, i) => {
      const accessDenied =
        authContext.cognitoUser &&
        !route.groups.includes(authContext.cognitoUser.currentGroup.groupID);

      if (route.subNavigations.length) {
        return [
          <Route
            key={`private_route_${i}`}
            path={route.path}
            element={
              accessDenied ? (
                <NoRouteAccess groups={route.groups} />
              ) : (
                route.view
              )
            }
          />,
          ...getRoutes(route.subNavigations),
        ];
      } else {
        return [
          <Route
            key={`private_route_${i}`}
            path={route.path}
            element={
              accessDenied ? (
                <NoRouteAccess groups={route.groups} />
              ) : (
                route.view
              )
            }
          />,
        ];
      }
    });
  };

  useEffect(() => {
    document.body.style.backgroundColor =
      isPriceListRoute || isBuyingRoute
        ? "#ffffff !important"
        : "#f7f7f7 !important";

    return () => {
      document.body.style.backgroundColor = "#f7f7f7 !important";
    };
  }, [isPriceListRoute]);

  if (isPriceListRoute) {
    return <ProductPriceListPage />;
  }

  if (isBuyingRoute) {
    return <ProductToBuyListPage />;
  }

  useEffect(() => {
    if (!authContext.initAuth || authContext.isLoading) {
      return;
    }

    if (!authContext.isAuth || !isAuthRoute(pathname)) {
      return;
    }

    navigate("/");
  }, [
    authContext.isLoading,
    authContext.isAuth,
    authContext.initAuth,
    navigate,
    pathname,
  ]);

  useEffect(() => {
    if (!authContext.initAuth || authContext.isLoading) {
      return;
    }

    if (authContext.isAuth || isAuthRoute(pathname)) {
      return;
    }

    navigate(AuthRoutes.login);
  }, [
    authContext.isLoading,
    authContext.isAuth,
    authContext.initAuth,
    navigate,
    pathname,
  ]);

  return (
    <Layout>
      <Suspense
        fallback={
          <Loading description="Bitte warten. Die Seite wird geladen..." />
        }
      >
        <Routes>
          {getRoutes(RoutesArray)}

          {/* If user is logged in and reaches an authentication route then redirect him to home. */}
          {AuthRouting.map((route, i) => (
            <Route
              key={`route_${i}`}
              path={route.path}
              element={<AuthLayout>{route.view}</AuthLayout>}
            />
          ))}

          {/* If user reaches an unknown route then redirect him to the error page NoRouteMatch. */}
          <Route key="nomatch" path="*" element={<NoRouteMatch />} />
        </Routes>
      </Suspense>
    </Layout>
  );
};

export default App;
